
import { useCallback, useState } from 'react';
import { Check, Copy, Crown, Info, TrendingUp } from 'lucide-react';
import { type PowerInfo } from '@/lib/powers-data';
import { copyText } from '@/components/help-center/latest-power-shared';

interface PowerEverypowerSectionProps {
  power: PowerInfo;
}

export function CopyCodeButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyText(code);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }, [code]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/60"
    >
      <code className="font-mono text-xs font-semibold text-gray-800">{code}</code>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-emerald-600" aria-hidden />
      )}
    </button>
  );
}

/** Avisos exibidos abaixo da galeria de peões (secção Smilies e Peões). */
export function EverypowerPawnsFootnotes() {
  return (
    <div className="mt-10 space-y-4 border-t border-gray-100 pt-10">
      <div
        className="rounded-xl border p-4"
        style={{
          borderColor: 'rgba(90, 185, 237, 0.45)',
          backgroundColor: 'rgba(90, 185, 237, 0.12)',
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-4 w-4" style={{ color: '#3a8ec4' }} aria-hidden />
          <p className="font-poppins text-sm font-semibold" style={{ color: '#1e5f8a' }}>
            Supporter
          </p>
        </div>
        <p className="font-inter text-sm leading-relaxed" style={{ color: '#1e5f8a' }}>
          Se você é Supporter, desactive essa opção nas configurações para que qualquer peão
          EveryPower funcione correctamente.
        </p>
      </div>

      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="mb-1 font-poppins text-xs font-semibold uppercase tracking-wide text-gray-500">
          Nota histórica
        </p>
        <p className="font-inter text-sm leading-relaxed text-gray-600">
          O peão Rubi que antes estava ligado ao EveryPower passou a ser um power à parte.
        </p>
      </div>
    </div>
  );
}

export default function PowerEverypowerSection({ power }: PowerEverypowerSectionProps) {
  if (power.slug !== 'everypower') return null;

  return (
    <section className="border-b border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50">
            <Crown className="h-4 w-4 text-emerald-600" aria-hidden />
          </div>
          <div>
            <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-emerald-600">
              Funcionalidade especial
            </p>
            <h2 className="font-poppins text-xl font-bold text-gray-900">EveryPower</h2>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3 font-inter text-base leading-relaxed text-gray-600">
            <p>
              EveryPower desbloqueia o <strong className="font-semibold text-gray-800">peão Esmeralda</strong>{' '}
              quando você possui pelo menos um exemplar de cada power já lançado no xat. Novos powers
              são lançados semanalmente — é preciso adquiri-los para manter o EveryPower activo.
            </p>
            <p>
              As variantes de cor do peão e os respectivos códigos estão na secção{' '}
              <strong className="font-semibold text-gray-800">Smilies e Peões</strong> mais abaixo.
              Com o power <strong className="font-semibold text-gray-800">Hat</strong> activo, as cores
              extra combinam com chapéus genéricos (ex.:{' '}
              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">(hat#Ekr)</code>) e também
              funcionam com Flashrank.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" aria-hidden />
                <p className="font-poppins text-sm font-semibold text-blue-900">Lista de visitantes</p>
              </div>
              <p className="font-inter text-base leading-relaxed text-blue-800/90">
                EveryPower posiciona você acima de todos os outros utilizadores do mesmo cargo na
                lista de visitantes. Complementa o efeito do power Topman no sistema de ranking.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Info className="h-4 w-4 text-gray-600" aria-hidden />
                <p className="font-poppins text-sm font-semibold text-gray-900">Desactivar</p>
              </div>
              <p className="mb-2 font-inter text-base leading-relaxed text-gray-600">
                Para desligar o EveryPower temporariamente, digite no chat ou desligue na lista de
                Powers:
              </p>
              <CopyCodeButton code="/-everypower" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
